import React, { useState, useEffect } from 'react';

interface NavBarProps {
  onApplyClick: () => void;
}

const NavBar: React.FC<NavBarProps> = ({ onApplyClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd < -50) {
      // Swipe down detected
      setIsMenuOpen(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMenuOpen(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white border-b-2 border-navy z-50 shadow-navbar-white h-16 md:h-20 pb-4">
      <div className="max-w-7xl mx-auto px-4 md:px-10 h-full">
        <div className="flex items-center justify-between h-full">
          <div className="flex items-center gap-4 md:gap-4 flex-shrink-0">
            <img 
              src="/tra.png" 
              alt="Inner Wealth Coaching Logo" 
              className="h-12 md:h-16 sm: mb-2 w-auto object-contain flex-shrink-0"
            />
            <span className="text-base md:text-xl font-light text-navy whitespace-nowrap">Inner Wealth Coaching</span>
          </div>

          {/* Desktop Navigation */}
          {windowWidth > 955 && (
          <div className="flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-navy hover:text-blue transition-colors"
            >
              <span className="nav-item-roll">HOME</span>
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-navy hover:text-blue transition-colors"
            >
              <span className="nav-item-roll">ABOUT</span>
            </button>
            <button
              onClick={() => scrollToSection('offer')}
              className="text-navy hover:text-blue transition-colors"
            >
              <span className="nav-item-roll">THE OFFER</span>
            </button>
            <button
              onClick={() => scrollToSection('who-this-is-for')}
              className="text-navy hover:text-blue transition-colors"
            >
              <span className="nav-item-roll">WHO THIS<br />IS FOR</span>
            </button>
            <button
              onClick={onApplyClick}
              className="btn-primary-small"
            >
              <span className="nav-item-roll">Apply Now</span>
            </button>
          </div>
          )}

          {/* Mobile Menu Button */}
          {windowWidth <= 955 && (
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="mr-4"
            style={{ color: '#0a1f3d' }}
          >
            <svg
              className="w-6 h-6 transition-all duration-300 ease-in-out"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 8h16M4 16h16" />
              )}
            </svg>
          </button>
          )}
        </div>

      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && windowWidth <= 955 && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Mobile Menu - Slides up from bottom */}
      {windowWidth <= 955 && (
      <div 
        className="fixed left-0 right-0 z-[110] transition-transform duration-300 ease-in-out shadow-2xl bg-white"
        style={{ 
          borderTopLeftRadius: '1.5rem', 
          borderTopRightRadius: '1.5rem',
          bottom: 0,
          transform: isMenuOpen ? 'translateY(0)' : 'translateY(100%)',
          maxHeight: '70vh'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="px-6 py-4 space-y-2 overflow-y-auto" style={{ maxHeight: '70vh' }}>
          <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setIsMenuOpen(false)}
              className="text-navy"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <button
            onClick={() => scrollToSection('home')}
            className="block w-full text-left text-navy hover:text-blue transition-colors py-3 text-lg border-b border-gray-200"
          >
            HOME
          </button>
          <button
            onClick={() => scrollToSection('about')}
            className="block w-full text-left text-navy hover:text-blue transition-colors py-3 text-lg border-b border-gray-200"
          >
            ABOUT
          </button>
          <button
            onClick={() => scrollToSection('offer')}
            className="block w-full text-left text-navy hover:text-blue transition-colors py-3 text-lg border-b border-gray-200"
          >
            THE OFFER
          </button>
          <button
            onClick={() => scrollToSection('who-this-is-for')}
            className="block w-full text-left text-navy hover:text-blue transition-colors py-3 text-lg border-b border-gray-200"
          >
            WHO THIS IS FOR
          </button>
          <button
            onClick={onApplyClick}
            className="btn-primary w-full mt-4"
          >
            Apply Now
          </button>
        </div>
      </div>
      )}
    </nav>
  );
};

export default NavBar;
